import { callOnReviewSubmit } from 'util/turnto-utils'
import checkSellerRatingEligibileService from 'services/api/check-seller-rating-eligibile'
import { hostsConfig } from 'init/config'
import globalConfig from 'init/global.config'
import globalStore from 'init/global-store'
import { DoMoreProps } from 'util/interfaces'
import msg from 'services/msg'
import reviewSubmissionConfig from 'widgets/submission/review-submission.config'
import srUtils from 'util/seller-rating-utils'
import ssoService from 'services/sso-service'
import submissionStore, { LaunchData, Source, SubmissionPageName, SubmissionPage }
  from 'store/submission-store'
import submissionLoader from 'widgets/submission/submission-loader'
import tokenService from 'services/token-service'
import { TOKEN_PARAM_NAME, TURNTO_EMAIL_TYPE, TurnToFlow, TURNTOSKU_PARAM }
  from './email-flows-common'
import waitForValue from 'util/wait-for-value'

export default {
  process(
    flow: TurnToFlow,
    params: URLSearchParams
  ): Promise<boolean> {
    return new Promise((resolve) => {
      if (!isReviewFlow(flow, params)) {
        resolve(false)
        return
      }

      if (flow === 'optout') {
        import(/* webpackChunkName: 'standalone-popover' */ 'widgets/standalone-popover/standalone-popover-loader').then((popoverLoader) => {
          popoverLoader.default.load({ message: msg('flowMsg.optout.rse') })
        })
        resolve(true)
        return
      }

      let page = 'review-form' as SubmissionPageName
      let launchData = {} as LaunchData
      let props = {}
      if (flow === 'review') {
        launchData = getRseLaunchData(params)
        if (launchData.startAction === 'visual') {
          page = 'share-media'
        }
      } else if (flow === 'rebelmail') {
        launchData = getRebelmailLaunchData(params)
        if (launchData.reviewId) { // was successfully submitted
          page = 'do-more'
          props = { msgType: 'review' } as DoMoreProps
          // call to onReviewSubmit handler.  will only be called if it exists
          callOnReviewSubmit(launchData.reviewId)
          // if device fingerprinting is on, this will submit it
          saveFingerprint(launchData)
        } else if (launchData.alreadyReviewed) {
          page = 'do-more'
          props = { msgType: 'alreadyReviewed' } as DoMoreProps
        }
      } else if (flow === 'revpr') {
        // coming from a purchase required confirmation email and should already be logged in from
        // email-flow-entry. now place the user on the review page
        launchData.startAction = 'review'
      }
      // if we have a RSE token, need to remove currently logged in user (if any)
      if (launchData.rseToken) {
        tokenService.logoutUser()
      }
      // for successful inbox submission flow, need to check for seller rating eligibility.
      // Since need to make server call, needs to be async and wait before proceeding.
      // Ideally would do with await/async, but not available in all browsers, so we have this
      // nested promises situation
      checkSellerRatingEligibile(launchData, params).then((transactionId) => {
        if (transactionId) {
          // because we are coming from an RSE, the token needs to be stored in review launch data
          // so the seller rating form knows which initData method to call
          submissionStore.launchData = { rseToken: launchData.rseToken }
          submissionStore.startSellerRatingFlow(
            hostsConfig.siteKey,
            { page: 'seller-rating-form', props: { msgType: 'reviewed' } },
            { transactionId }
          )
          submissionLoader.load().then(() => resolve(true)).catch(() => {})
          return
        }
        startRevFlow(flow, launchData, params, { page, props }).then(() => resolve(true))
          .catch(() => {})
      }).catch(() => {
        startRevFlow(flow, launchData, params, { page, props }).then(() => resolve(true))
          .catch(() => {})
      })
    })
  }
}

function startRevFlow(
  flow: TurnToFlow,
  launchData: LaunchData,
  params: URLSearchParams,
  page: SubmissionPage
): Promise<void> {
  return new Promise((resolve) => {
    waitForValue(() => params.get(TURNTOSKU_PARAM) || globalStore.sku).then((sku: string) => {
      if (globalConfig.ssoEnabled && ['review', 'rebelmail', 'share-media'].indexOf(flow) > -1) {
        ssoService.ssoGetLoggedInData(sku, page, launchData, 'rev')
        resolve()
        return
      }
      submissionStore.startRevFlow(sku, hostsConfig.siteKey, page, launchData)
      submissionLoader.load().then(() => resolve()).catch(() => {})
    }).catch(() => {})
  })
}
// if yes, then resolves with transaction id, otherwise resolves to no
function checkSellerRatingEligibile(
  launchData: LaunchData,
  params: URLSearchParams
): Promise<number> {
  return new Promise((resolve) => {
    if (launchData.reviewId && params.get('turntoCheckSrEligibility') === '1') {
      checkSellerRatingEligibileService.checkSellerRatingEligibile({
        'context-token': launchData.rseToken,
        lastSeenSrDate: srUtils.getLastSeenSellerRatingDate()
      }).then((data) => {
        resolve(data.transactionId)
      }).catch(() => {
        resolve(null)
      })
    } else {
      resolve(null)
    }
  })
}

function saveFingerprint(launchData: LaunchData) {
  // if deviceFingerpring collection is on and a successful inbox submission just happened, this
  // will save the fingerprint
  if (reviewSubmissionConfig.deviceFingerprintOn && launchData.reviewId) {
    import(/* webpackChunkName: 'dfp' */ 'services/device-fingerprint').then((fingerprintService) => {
      fingerprintService.default.saveFingerprint(launchData.reviewId, launchData.rseToken)
    })
  }
}

function getRseLaunchData(params: URLSearchParams) {
  const launchData = {
    rseToken: params.get(TOKEN_PARAM_NAME) || params.get('token'),
    source: params.get('turntoEmailType') === 'smsReviewSolicitation' ? Source.RSSMS : Source.RSE
  } as LaunchData
  if (params.get('reviewStartAction')) {
    launchData.startAction = params.get('reviewStartAction') === 'shareAPhoto' ? 'visual' : 'review'
  }
  if (params.get('starSelected')) {
    launchData.starSelected = parseInt(params.get('starSelected')) || 0
    if (launchData.starSelected < 0 || launchData.starSelected > 5) {
      launchData.starSelected = 0
    }
  }
  if (params.get('turntoIncentiveType')) {
    launchData.incentiveType = params.get('turntoIncentiveType')
  }
  if (params.get('turntoShowGuidelines') === '1') {
    launchData.showGuidelines = true
  }
  if (params.get('turntoCampaign')) {
    launchData.campaign = params.get('turntoCampaign')
  }

  return launchData
}

function getRebelmailLaunchData(params: URLSearchParams) {
  return {
    alreadyReviewed: params.get('turntotoAlreadyReviewed') === '1',
    hasErrors: params.get('turntoReviewHasErrors') ? true : false,
    showGuidelines: params.get('turntoShowGuidelines') === '1' ? true : false,
    startAction: 'review',
    rseToken: params.get(TOKEN_PARAM_NAME) || params.get('token'),
    reviewId: params.get('turntoReviewId') ? parseInt(params.get('turntoReviewId')) : null,
    source: Source.RSE
  } as LaunchData
}

function isReviewFlow(flow: TurnToFlow, params: URLSearchParams) {
  // If flow is 'optout', need to make sure it's from an RSE. Could be from question email.
  // eslint-disable-next-line max-len
  return (flow === 'optout' && (['reviewSolicitation', 'rseFollowUp', 'smsReviewSolicitation'].indexOf(params.get(TURNTO_EMAIL_TYPE)) > -1)) ||
    ['review', 'rebelmail', 'revpr'].indexOf(flow) > -1
}
